<template>
  <div class="sales-reports-page">
    <KTCodePreview title="">
      <template #preview>
        <div v-if="!checkPermission('SALES_REPORT_VIEW')">
          <b-alert
            variant="warning"
            show
          >
            Bạn chưa có quyền xem báo cáo bán hàng.
          </b-alert>
        </div>

        <div
          v-else
          class="sales-reports-shell"
        >
          <div
            v-if="visibleTabs.length"
            class="tab-nav mb-3"
          >
            <button
              v-for="(tab, index) in visibleTabs"
              :key="tab.key"
              type="button"
              class="tab-nav-btn"
              :class="{ active: activeTabIndex === index }"
              @click="onTabChange(index)"
            >
              {{ tab.label }}
            </button>
          </div>

          <template v-if="activeTab">
            <SalesReportsPageHeader
              :title="activeTab.title || activeTab.label"
              :api-path="isOverviewTab ? '' : activeTab.apiPath"
              :api-method="activeTab.apiMethod || 'GET'"
              :subtitle="headerSubtitle"
              :loading="loading || exportLoading"
              :show-export="showHeaderExport"
              :can-export="canExportExcel"
              @refresh="refreshAll"
              @export="handleExport"
            />

            <b-alert
              v-if="activeTab.key !== 'overview' && !canViewActiveTabLegacy"
              variant="warning"
              show
              class="mb-3"
            >
              Bạn chưa có quyền xem báo cáo này. Liên hệ quản trị để được cấp:
              <strong>{{ missingLegacyPermissions.join(', ') }}</strong>
            </b-alert>

            <SalesReportsAdvancedFilter
              v-if="showAdvancedFilter"
              :filters="filters"
              :stores="stores"
              :filter-fields="activeTab.filterFields || []"
              :advanced-fields="activeTab.advancedFields || []"
              :multi-select-advanced="Boolean(activeTab.multiSelectAdvanced)"
              :options="filterOptions"
              :search-placeholder="activeTab.searchPlaceholder"
              :hide-date-filter="activeTab.hideDateFilter"
              :loading="loading"
              @apply="handleApplyFilter"
              @reset="handleResetFilter"
            />
            <SalesReportsInlineFilter
              v-else-if="showInlineFilter"
              :filters="filters"
              :stores="stores"
              :tab-key="activeTab.key"
              :filter-fields="activeTab.filterFields || ['date', 'store']"
              :search-placeholder="activeTab.searchPlaceholder"
              :hide-date-filter="activeTab.hideDateFilter"
              :loading="loading"
              @apply="handleApplyFilter"
              @reset="handleResetFilter"
            />

            <SalesReportsOverviewPanel
              v-if="activeTab.key === 'overview'"
              :kpi-cards="tabKpiCards"
              :visible-tab-keys="visibleTabKeys"
              @navigate="navigateToReport"
            />

            <SalesReportsBulkImeiCheck
              v-else-if="activeTab.layout === 'bulk-imei' && canViewActiveTabLegacy"
            />

            <template v-else-if="canViewActiveTabLegacy">
              <SalesReportsTabKpis :cards="tabKpiCards" />

              <b-row
                v-if="showChartSection"
                class="mb-3"
              >
                <b-col
                  cols="12"
                  :lg="activeTab.layout === 'split' ? 6 : 12"
                >
                  <SalesReportsCharts
                    :chart-type="activeTab.chartType"
                    :title="activeTab.chartTitle"
                    :rows="chartRows"
                    :loading="loading"
                    :primary-color="chartPrimaryColor"
                  />
                </b-col>
                <b-col
                  v-if="activeTab.layout === 'split'"
                  cols="12"
                  lg="6"
                >
                  <SalesReportsTable
                    :columns="tableColumns"
                    :rows="displayRows"
                    :loading="loading"
                    :show-pagination="activeTab.paginated"
                    :current-page="pagination.page"
                    :per-page="pagination.limit"
                    :total-items="pagination.totalItems"
                    :total-pages="pagination.totalPage"
                    :tab-key="activeTab.key"
                    :table-title="splitTableTitle"
                    :total-row="tableTotalRow"
                    @page-change="handlePageChange"
                  />
                </b-col>
              </b-row>

              <SalesReportsTable
                v-if="activeTab.layout !== 'split'"
                :columns="tableColumns"
                :rows="displayRows"
                :loading="loading"
                :show-pagination="activeTab.paginated"
                :current-page="pagination.page"
                :per-page="pagination.limit"
                :total-items="pagination.totalItems"
                :total-pages="pagination.totalPage"
                :tab-key="activeTab.key"
                :table-title="activeTab.tableTitle"
                :total-row="tableTotalRow"
                @page-change="handlePageChange"
              />
            </template>
          </template>

        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import moment from 'moment';

import KTCodePreview from '@/view/content/CodePreview.vue';
import permissionMixin from '@/view/mixins/permission.mixin';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { makeToastFaile } from '@/utils/common';

import SalesReportsService from './services/SalesReportsService';
import {
  BULK_IMEI_MAX,
  OVERVIEW_SUBTITLE,
  SALES_REPORT_DEFAULT_PAGE_SIZE,
  SALES_REPORT_TABS,
} from './constants';
import { buildTabKpis } from './utils/tabKpis';
import { buildTableTotalRowFromApi } from './utils/reportAggregates';
import {
  buildDealSheetColumns,
  collectDealSheetDefinitions,
  normalizeRowsDealSheets,
} from './utils/dealSheetColumns';
import {
  formatSalesReportPeriodLabel,
  resolveSalesReportDateRange,
} from './utils/dateFilter';
import SalesReportsPageHeader from './components/SalesReportsPageHeader.vue';
import SalesReportsInlineFilter from './components/SalesReportsInlineFilter.vue';
import SalesReportsAdvancedFilter from './components/SalesReportsAdvancedFilter.vue';
import SalesReportsTabKpis from './components/SalesReportsTabKpis.vue';
import SalesReportsCharts from './components/SalesReportsCharts.vue';
import SalesReportsTable from './components/SalesReportsTable.vue';
import SalesReportsOverviewPanel from './components/SalesReportsOverviewPanel.vue';
import SalesReportsBulkImeiCheck from './components/SalesReportsBulkImeiCheck.vue';

const initialDateRange = () => {
  const today = moment();
  return {
    startDate: today.clone().subtract(9, 'days').format('DD/MM/YYYY'),
    endDate: today.format('DD/MM/YYYY'),
  };
};

export default {
  name: 'SalesReportsHub',
  components: {
    KTCodePreview,
    SalesReportsPageHeader,
    SalesReportsInlineFilter,
    SalesReportsAdvancedFilter,
    SalesReportsTabKpis,
    SalesReportsCharts,
    SalesReportsTable,
    SalesReportsOverviewPanel,
    SalesReportsBulkImeiCheck,
  },
  mixins: [permissionMixin],
  data() {
    const dateRange = initialDateRange();
    return {
      loading: false,
      stores: [],
      tabs: SALES_REPORT_TABS,
      activeTabIndex: 0,
      filters: {
        ...dateRange,
        storeId: null,
        storeIds: [],
        billType: null,
        billTypes: [],
        searchText: '',
        storeIdA: null,
        storeIdB: null,
        productSearch: '',
        brandId: null,
        brandIds: [],
        categoryId: null,
        categoryIds: [],
        providerId: null,
        providerIds: [],
        subProviderIds: [],
        customerId: null,
        spc: '',
        imeiCode: null,
        productId: null,
      },
      filterOptions: { brands: [], categories: [], providers: [] },
      filterOptionsLoaded: false,
      rows: [],
      pagination: {
        page: 1,
        limit: SALES_REPORT_DEFAULT_PAGE_SIZE,
        totalItems: 0,
        totalPage: 0,
      },
      summaryData: null,
      totalsData: null,
      inventoryTotal: null,
      revenueByDay: [],
      storeCount: 0,
      reportTotalRow: null,
      overviewData: {
        revenue: 0,
        grossProfit: 0,
        margin: 0,
        inventoryValue: 0,
        skuCount: 0,
      },
      hasInitialized: false,
      exportLoading: false,
    };
  },
  computed: {
    visibleTabs() {
      if (!this.checkPermission('SALES_REPORT_VIEW')) {
        return [];
      }
      return this.tabs;
    },
    canViewActiveTabLegacy() {
      if (!this.activeTab?.legacyPermissions?.length) {
        return true;
      }
      return this.checkAllPermissions(this.activeTab.legacyPermissions);
    },
    missingLegacyPermissions() {
      if (!this.activeTab?.legacyPermissions?.length) {
        return [];
      }
      return this.activeTab.legacyPermissions.filter(
        (permission) => !this.checkPermission(permission),
      );
    },
    activeTab() {
      return this.visibleTabs[this.activeTabIndex] || null;
    },
    isOverviewTab() {
      return this.activeTab?.key === 'overview';
    },
    headerSubtitle() {
      if (this.isOverviewTab) {
        return OVERVIEW_SUBTITLE;
      }
      if (this.isBulkImeiTab) {
        return `Tối đa ${BULK_IMEI_MAX.toLocaleString('vi-VN')} IMEI/lần`;
      }
      return this.pageSubtitle;
    },
    visibleTabKeys() {
      return this.visibleTabs.map((tab) => tab.key);
    },
    isBulkImeiTab() {
      return this.activeTab?.layout === 'bulk-imei';
    },
    showHeaderExport() {
      return !this.isOverviewTab && !this.isBulkImeiTab && this.activeTab?.showExport !== false;
    },
    showAdvancedFilter() {
      return (
        this.activeTab &&
        this.activeTab.key !== 'overview' &&
        this.canViewActiveTabLegacy &&
        this.activeTab.filterMode === 'advanced'
      );
    },
    showInlineFilter() {
      return (
        this.activeTab &&
        this.activeTab.key !== 'overview' &&
        this.canViewActiveTabLegacy &&
        this.activeTab.filterMode !== 'advanced' &&
        this.activeTab.layout !== 'bulk-imei'
      );
    },
    activeExportPermission() {
      return this.activeTab?.exportPermission || 'SALES_REPORT_EXPORT';
    },
    canExportExcel() {
      return (
        !this.isOverviewTab &&
        !this.isBulkImeiTab &&
        this.checkPermission(this.activeExportPermission) &&
        this.canViewActiveTabLegacy
      );
    },
    pageSubtitle() {
      if (this.activeTab?.snapshotSubtitle) {
        const count = this.pagination.totalItems || this.rows.length;
        return `Snapshot hiện tại · ${count} kho`;
      }
      const period = formatSalesReportPeriodLabel(this.filters);
      const count = this.pagination.totalItems || this.rows.length;
      const suffix = this.activeTab?.periodSuffix;
      if (this.activeTab?.key === 'by-store-category') {
        return `${period} · ${this.storeCount} cửa hàng · ${count} dòng`;
      }
      if (suffix) {
        return `${period} · ${count} ${suffix}`;
      }
      return `${period} · ${count} dòng`;
    },
    tabKpiCards() {
      return buildTabKpis({
        tabKey: this.activeTab?.key,
        summary: this.summaryData,
        totals: this.totalsData,
        totalRow: this.reportTotalRow,
        total: this.inventoryTotal,
        pagination: this.pagination,
        rows: this.rows,
        overview: this.overviewData,
        storeCount: this.storeCount,
      });
    },
    showChartSection() {
      return Boolean(this.activeTab?.chartType);
    },
    chartRows() {
      if (this.revenueByDay.length) {
        const chartTabs = ['by-bill', 'by-product'];
        if (chartTabs.includes(this.activeTab?.key)) {
          return this.revenueByDay;
        }
      }
      if (this.activeTab?.key === 'by-store-category') {
        const byCategory = {};
        this.rows.forEach((row) => {
          const key = row.cayDanhMuc || '—';
          byCategory[key] = (byCategory[key] || 0) + Number(row.doanhThu || 0);
        });
        return Object.entries(byCategory).map(([cayDanhMuc, doanhThu]) => ({
          cayDanhMuc,
          doanhThu,
        }));
      }
      return this.rows;
    },
    chartPrimaryColor() {
      return this.activeTab?.chartColor || '#3B82F6';
    },
    splitTableTitle() {
      if (this.activeTab?.key === 'by-bill-type') {
        return 'Chi tiết theo loại HĐ';
      }
      if (this.activeTab?.key === 'by-customer') {
        return 'Chi tiết khách hàng';
      }
      return 'Chi tiết';
    },
    tableTotalRow() {
      return buildTableTotalRowFromApi(this.activeTab?.key, {
        summary: this.summaryData,
        totals: this.totalsData,
        totalRow: this.reportTotalRow,
        rows: this.rows,
      });
    },
    tableColumns() {
      const columns = this.activeTab?.columns || [];
      if (this.activeTab?.key !== 'by-imei') {
        return columns;
      }
      const dealSheetDefs = collectDealSheetDefinitions(this.rows);
      return buildDealSheetColumns(columns, dealSheetDefs);
    },
    displayRows() {
      if (this.activeTab?.key !== 'by-imei') {
        return this.rows;
      }
      return normalizeRowsDealSheets(this.rows);
    },
  },
  watch: {
    '$route.query.tab': {
      immediate: true,
      handler(value) {
        if (!value) {
          return;
        }
        const tabIndex = this.visibleTabs.findIndex((tab) => tab.key === value);
        if (tabIndex >= 0) {
          this.activeTabIndex = tabIndex;
        }
      },
    },
    visibleTabs: {
      immediate: true,
      handler() {
        if (!this.hasInitialized && this.checkPermission('SALES_REPORT_VIEW') && this.visibleTabs.length) {
          this.initializeData();
        }
      },
    },
  },
  created() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Báo cáo', route: '/reports/accounting/cash' },
      { title: 'Báo cáo bán hàng' },
    ]);
  },
  methods: {
    async initializeData() {
      if (!this.checkPermission('SALES_REPORT_VIEW') || !this.visibleTabs.length) {
        return;
      }
      try {
        this.hasInitialized = true;
        this.loading = true;
        this.stores = await SalesReportsService.fetchStores();
        const firstStore = this.stores[0] || null;
        this.filters.storeIdA = firstStore ? firstStore.id : null;
        this.filters.storeIdB = this.stores[1] ? this.stores[1].id : this.filters.storeIdA;
        if (this.activeTab?.paginated) {
          this.pagination.limit = this.activeTab.key === 'by-imei' ? 50 : 50;
        }
        await this.refreshAll();
      } catch (error) {
        this.hasInitialized = false;
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi tải dữ liệu');
      } finally {
        this.loading = false;
      }
    },
    async fetchOverviewData() {
      this.overviewData = await SalesReportsService.fetchOverview(this.filters);
    },
    async fetchTabData() {
      if (
        !this.activeTab ||
        this.activeTab.key === 'overview' ||
        this.activeTab.layout === 'bulk-imei'
      ) {
        this.rows = [];
        this.summaryData = null;
        this.totalsData = null;
        this.inventoryTotal = null;
        this.revenueByDay = [];
        return;
      }
      if (!this.canViewActiveTabLegacy) {
        this.rows = [];
        this.summaryData = null;
        this.totalsData = null;
        this.inventoryTotal = null;
        this.revenueByDay = [];
        this.pagination.totalItems = 0;
        this.pagination.totalPage = 0;
        return;
      }
      const result = await SalesReportsService.fetchTabData(
        this.activeTab,
        this.filters,
        this.pagination,
      );
      this.rows = result.rows;
      if (result.pagination) {
        this.pagination.page = result.pagination.page;
        this.pagination.limit = result.pagination.limit;
        this.pagination.totalItems = result.pagination.totalItems;
        this.pagination.totalPage = result.pagination.totalPage;
      } else {
        this.pagination.totalItems = result.rows.length;
        this.pagination.totalPage = 1;
      }
      this.storeCount = result.storeCount ?? 0;
      this.reportTotalRow = result.totalRow || null;
      // Tab có summaryEndpoint riêng: summaryData được fetchTabSummary quản lý, không ghi đè ở đây
      if (!this.activeTab?.summaryEndpoint) {
        this.summaryData = result.summary || null;
      }
      this.totalsData = result.totals || null;
      this.inventoryTotal = result.total || null;
      this.revenueByDay = result.revenueByDay || [];
    },
    async fetchTabSummary() {
      if (!this.activeTab?.summaryEndpoint) return;
      try {
        this.summaryData = await SalesReportsService.fetchTabSummary(
          this.activeTab,
          this.filters,
        );
      } catch (_) {
        this.summaryData = null;
      }
    },
    async refreshAll() {
      if (this.isBulkImeiTab) {
        // Tab check IMEI hàng loạt tự quản lý dữ liệu trong component bespoke.
        return;
      }
      if (this.activeTab?.filterMode === 'advanced') {
        this.ensureFilterOptions();
      }
      try {
        this.loading = true;
        if (this.isOverviewTab) {
          this.rows = [];
          this.summaryData = null;
          this.totalsData = null;
          this.inventoryTotal = null;
          this.revenueByDay = [];
          await this.fetchOverviewData();
        } else {
          await Promise.all([this.fetchTabData(), this.fetchTabSummary()]);
        }
      } catch (error) {
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi làm mới dữ liệu');
      } finally {
        this.loading = false;
      }
    },
    async ensureFilterOptions() {
      if (this.filterOptionsLoaded) {
        return;
      }
      this.filterOptionsLoaded = true;
      try {
        this.filterOptions = await SalesReportsService.fetchFilterOptions();
      } catch (error) {
        // Bộ lọc nâng cao vẫn dùng được dù thiếu option (chỉ mất gợi ý dropdown).
        this.filterOptionsLoaded = false;
      }
    },
    async navigateToReport(tabKey) {
      const index = this.visibleTabs.findIndex((tab) => tab.key === tabKey);
      if (index >= 0) {
        await this.onTabChange(index);
      }
    },
    async onTabChange(index) {
      if (this.activeTabIndex === index) {
        return;
      }
      this.activeTabIndex = index;
      this.pagination = {
        ...this.pagination,
        page: 1,
        limit: SALES_REPORT_DEFAULT_PAGE_SIZE,
      };
      this.summaryData = null;
      this.totalsData = null;
      this.inventoryTotal = null;
      this.revenueByDay = [];
      this.reportTotalRow = null;
      this.storeCount = 0;
      this.rows = [];
      this.$router.replace({
        query: {
          ...this.$route.query,
          tab: this.activeTab ? this.activeTab.key : undefined,
        },
      });
      await this.refreshAll();
    },
    async handleApplyFilter(nextFilters) {
      this.filters = { ...this.filters, ...nextFilters };
      const dateRange = resolveSalesReportDateRange(this.filters);
      if (!dateRange.valid) {
        makeToastFaile(dateRange.message);
        return;
      }
      this.pagination.page = 1;
      await this.refreshAll();
    },
    async handleResetFilter() {
      const dateRange = initialDateRange();
      const firstStore = this.stores[0] || null;
      this.filters = {
        ...dateRange,
        storeId: null,
        storeIds: [],
        billType: null,
        billTypes: [],
        searchText: '',
        storeIdA: firstStore ? firstStore.id : null,
        storeIdB: this.stores[1] ? this.stores[1].id : firstStore?.id || null,
        productSearch: '',
        brandId: null,
        brandIds: [],
        categoryId: null,
        categoryIds: [],
        providerId: null,
        providerIds: [],
        subProviderIds: [],
        customerId: null,
        spc: '',
        imeiCode: null,
        productId: null,
      };
      this.pagination.page = 1;
      await this.refreshAll();
    },
    async handlePageChange(page) {
      this.pagination.page = page;
      try {
        this.loading = true;
        await this.fetchTabData();
      } catch (error) {
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi tải trang');
      } finally {
        this.loading = false;
      }
    },
    async handleExport() {
      if (!this.activeTab || this.isOverviewTab || this.isBulkImeiTab) {
        return;
      }
      if (!this.checkPermission(this.activeExportPermission)) {
        makeToastFaile('Bạn chưa có quyền xuất Excel báo cáo này.');
        return;
      }
      if (!this.canViewActiveTabLegacy) {
        makeToastFaile('Bạn chưa có quyền xem báo cáo này.');
        return;
      }
      try {
        this.exportLoading = true;
        await SalesReportsService.exportTabExcel(this.activeTab, this.filters);
      } catch (error) {
        const message = await this.parseExportError(error);
        makeToastFaile(message);
      } finally {
        this.exportLoading = false;
      }
    },
    async parseExportError(error) {
      const blob = error?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const json = JSON.parse(text);
          return json.message || 'Lỗi xuất Excel';
        } catch (e) {
          return 'Lỗi xuất Excel';
        }
      }
      return error?.response?.data?.message || error.message || 'Lỗi xuất Excel';
    },
  },
};
</script>

<style scoped>
.sales-reports-shell {
  background: #f3f4f6;
  margin: -1rem -1.25rem 0;
  padding: 1rem 1.25rem 1.5rem;
  border-radius: 8px;
  min-height: 400px;
}
.tab-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}
.tab-nav-btn {
  border: none;
  background: transparent;
  color: #4b5563;
  font-size: 13px;
  font-weight: 500;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.tab-nav-btn:hover {
  background: #f3f4f6;
}
.tab-nav-btn.active {
  background: #111827;
  color: #fff;
}
</style>

<!--
  App-shell Metronic (.flex-root.flex-column) thiếu min-width:0 nên nội dung có
  min-content rộng (vd bảng "Bán theo IMEI" 19 cột) đẩy tràn cả layout — KPI và
  cột bị cắt ngoài viewport. min-width:0 cho cột nội dung co lại đúng bề ngang
  viewport, để bảng tự cuộn ngang trong khung (.lov-table-wrap) thay vì kéo giãn
  toàn trang. Rule global an toàn (chỉ cho phép co, không ép kích thước).
-->
<style>
.flex-root.flex-column {
  min-width: 0;
}
</style>
